import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { concatMap, map, Observable, switchMap, withLatestFrom, catchError, of } from 'rxjs';
import * as supportActions from '../store/index';
import * as supportSelectors from './support.selectors';
import { SupportApiService } from '../services/support-api.service';
import { CommandResponse } from 'src/app/shared/models/command-response';
import { QueryResponse } from 'src/app/shared/models/query-response';
import { TransferinatorAccountDetailsResponse } from '../services/models/transferinator-account-details.model';
import { MatSnackBar } from '@angular/material/snack-bar';
import { HttpErrorResponse } from '@angular/common/http';

@Injectable()
export class SupportEffects {
  private actions$ = inject(Actions);
  private store = inject(Store);
  private api = inject(SupportApiService);

  private _snackBar = inject(MatSnackBar);

  transferStudents$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(supportActions.actions.transferStudents),
      switchMap((action) =>
        this.api.saveStudentAccountTransfer(action.model).pipe(
          map((response: CommandResponse) => {
            if (response.isSuccess) {
              this.openSnackBar('Student(s) transferred successfully');
              return supportActions.actions.transferStudentsSuccess();
            } else {
              this.openSnackBar('Student(s) transfer failed');
              return supportActions.actions.transferStudentsFailed({
                error: 'Student(s) transfer failed'
              });
            }
          }),
          catchError(() => {
            this.openSnackBar('Student(s) transfer failed');
            return of(
              supportActions.actions.transferStudentsFailed({
                error: 'Student(s) transfer failed'
              })
            );
          })
        )
      )
    )
  );

  getTransferinatorAccountDetails$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(supportActions.actions.transferinatorAccountDetails),
      concatMap((action) =>
        this.api.getTransferinatorAccountDetails(action).pipe(
          map((response: QueryResponse<TransferinatorAccountDetailsResponse>) => {
            if (response.isSuccess) {
              return supportActions.actions.transferinatorAccountDetailsSuccess({
                response: response.result,
                getStudents: action.getStudents,
                id: action.accountId
              });
            } else {
              this.openSnackBar('Account Lookup failed');
              return supportActions.actions.transferStudentsFailed({
                error: 'Account Lookup failed'
              });
            }
          }),
          catchError((error: HttpErrorResponse) => {
            const errorMessage = error?.error?.errors
              ? Object.values(error.error.errors)[0] // take first value
              : error.message || 'Unexpected error';
            this.openSnackBar('Account Lookup failed: ' + errorMessage);
            return of(
              supportActions.actions.transferStudentsFailed({
                error: `${errorMessage}: ${action.accountId}`
              })
            );
          })
        )
      )
    )
  );

  cleverAccountLookup$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(supportActions.actions.cleverSupportPageActions.userSubmittedAccountSearch),
      switchMap((action) =>
        this.api.getCleverAccountLookup(action.accountId).pipe(
          map((response) => {
            if (response.isSuccess) {
              return supportActions.actions.cleverSupportAPIActions.accountSearchSuccess({
                result: response.result
              });
            } else {
              return supportActions.actions.cleverSupportAPIActions.accountSearchFailure({
                error: 'Account search failed, Please try again.'
              });
            }
          }),
          catchError(() => {
            this.openSnackBar('Account search failed, Please try again.');
            return of(
              supportActions.actions.cleverSupportAPIActions.accountSearchFailure({
                error: 'Account search failed, Please try again.'
              })
            );
          })
        )
      )
    )
  );

  updateCleverDetails$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(supportActions.actions.cleverSupportPageActions.userSubmittedNewCleverDetails),
      withLatestFrom(
        this.store.select(supportSelectors.getCleverCurrentAccountId),
        this.store.select(supportSelectors.getCleverCurrentAccountLookupMethod)
      ),
      switchMap(([action, accountId, lookupMethod]) =>
        this.api.updateCleverAccountDetails(accountId, action.districtName, action.ssoReference, lookupMethod).pipe(
          map((response) => {
            if (response.isSuccess) {
              return supportActions.actions.cleverSupportAPIActions.cleverAccountUpdateSuccess();
            } else {
              return supportActions.actions.cleverSupportAPIActions.cleverAccountUpdateFailure({
                error: 'Failed to update Clever account details'
              });
            }
          }),
          catchError(() => {
            this.openSnackBar('Failed to update Clever account details, Please try again.');
            return of(
              supportActions.actions.cleverSupportAPIActions.cleverAccountUpdateFailure({
                error: 'Failed to update Clever account details'
              })
            );
          })
        )
      )
    )
  );

  undeleterAccountLookup$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(supportActions.actions.undeleteSupportPageActions.userSubmittedAccountSearch),
      switchMap((action) =>
        this.api.getUndeleterAccountLookup(action.accountId).pipe(
          map((response) => {
            if (response.isSuccess) {
              return supportActions.actions.undeleteSupportAPIActions.accountSearchSuccess({
                result: { students: response.result.students }
              });
            } else {
              return supportActions.actions.undeleteSupportAPIActions.accountSearchFailure({
                error: 'Account search failed, Please try again.'
              });
            }
          }),
          catchError(() => {
            this.openSnackBar('Account search failed, Please try again.');
            return of(
              supportActions.actions.undeleteSupportAPIActions.accountSearchFailure({
                error: 'Account search failed, Please try again.'
              })
            );
          })
        )
      )
    )
  );

  restoreDeletedStudent$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(supportActions.actions.undeleteSupportPageActions.userRequestedStudentUndelete),
      switchMap((action) =>
        this.api.restoreDeletedStudent(action.studentId).pipe(
          map((response) => {
            if (response.isSuccess) {
              return supportActions.actions.undeleteSupportAPIActions.studentRestoreSuccess({
                studentId: action.studentId
              });
            } else {
              return supportActions.actions.undeleteSupportAPIActions.studentRestoreFailure({
                error: 'Failed to restore student'
              });
            }
          }),
          catchError(() => {
            this.openSnackBar('Failed to restore student, Please try again.');
            return of(
              supportActions.actions.undeleteSupportAPIActions.studentRestoreFailure({
                error: 'Failed to restore student'
              })
            );
          })
        )
      )
    )
  );

  openSnackBar(message: string) {
    this._snackBar.open(message, 'Close', {
      duration: 3000,
      horizontalPosition: 'right'
    });
  }
}
